// Browser-local "remember this device" state for MFA (PROMPT-04 / Audit §1.7).
//
// device_id  — a stable per-browser UUID (persisted once). Re-trusting the same
//              browser reuses this id so the server upserts a single row.
// token      — the rotating secret returned by the mfa-trust-device "issue" call.
//              The server only stores its SHA-256 hash (mfa_trusted_devices.token_hash);
//              the plaintext lives only here, in this browser.
//
// Both live in localStorage so trust survives IP changes and is browser-specific,
// which is what "trust this device" means. Clearing the token (or the server
// revoking the row) re-prompts for TOTP.

const DEVICE_ID_KEY = "forge.mfa.deviceId";
const TOKEN_KEY = "forge.mfa.deviceToken";

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Stable per-browser device id, created on first use. */
export function getOrCreateDeviceId(): string {
  const ls = safeLocalStorage();
  if (!ls) return randomId();
  let id = ls.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = randomId();
    ls.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/** The device id if one has been created (without creating one). */
export function getStoredDeviceId(): string | null {
  return safeLocalStorage()?.getItem(DEVICE_ID_KEY) ?? null;
}

/** The stored {deviceId, token} pair, or null if this browser isn't remembered. */
export function getStoredDeviceToken(): { deviceId: string; token: string } | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  const deviceId = ls.getItem(DEVICE_ID_KEY);
  const token = ls.getItem(TOKEN_KEY);
  if (!deviceId || !token) return null;
  return { deviceId, token };
}

export function storeDeviceToken(deviceId: string, token: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(DEVICE_ID_KEY, deviceId);
  ls.setItem(TOKEN_KEY, token);
}

/** Forget this browser's trust (keeps the stable device id so re-trust reuses the row). */
export function clearDeviceToken(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.removeItem(TOKEN_KEY);
}

/** A short human label for the current browser/OS, used as the device_label. */
export function describeCurrentDevice(): string {
  if (typeof navigator === "undefined") return "Trusted device";
  const ua = navigator.userAgent || "";
  let browser = "Browser";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua)) browser = "Safari";
  let os = "";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X|Macintosh/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return os ? `${browser} on ${os}` : browser;
}
